import * as React from "react";
import * as cx from "classnames";
import { DataSignal } from "s-js";
import SuperCritical from "lib/supercritical";
import { noDefault } from "lib/eventWrappers";
import { notNullish } from "lib/notNullish";
import TZDate from "lib/TZDate";
import { tzmoment, tzmomentMod } from "lib/tzmoment";

import { BerthReservation } from "data/models/BerthReservation";
import { BerthReservationStatus, BerthReservationStatusDisplayName } from "server/enums/BerthReservation";
import { CoordinationCardStatuses } from "data/models/CoordinationCardStatuses";
import { MilestoneTypes as MSType } from "data/models/MilestoneTypes";
import { ScheduleDateEntity } from "data/models/ScheduleDateEntity";
import { ScheduleDateType as SDType } from "data/models/ScheduleDateType";

import {
    BerthAssignmentViewModel,
    CCardCurrency,
    CancelledCCardViewModel,
    CurrentCCardViewModel,
    EditPhase,
    EditType,
    PortCallSchedule,
    PortCallMilestone
} from "./model";
import { PortCallScheduleHeader } from "./header";

import { BerthAssignmentDetailsView } from "portcall/berthassignmentdetails/view";
import {
    BerthStayDurationText,
    LogActualAttributionText,
    MetroMapFrameworkView,
    DateTypeParticiple as DefaultDateTypeParticiple,
    MilestoneHeaderDateText,
    MilestoneScheduleDetailsRowLabel,
    MovementToLocationText,
    MovementTransitDurationText,
    MovementTypeParticiple,
} from "portcall/metromapframework/view";
import { CCardStatusIndicator } from "portcall/ccardstatusindicator/view";
import { CancelledCoordinationCardDetailsView } from "portcall/coordinationcarddetails/cancelledview";
import { CoordinationCardDetailsView } from "portcall/coordinationcarddetails/view";
import { DateTimePicker } from "portcall/datetimepicker/view";
import { IssueCountPillBadgeWithTooltip } from "portcall/issues/view";
import { ListErrors } from "core/validationerror/view";

export interface PortCallScheduleViewParams {
    model: PortCallSchedule;
}

export const PortCallScheduleView = SuperCritical(({ model, isAddPC } : {
    model : PortCallSchedule,
    isAddPC?: boolean,
}) =>
    <div className="portcall-schedule">
        <MainHeader model={model.Header} />
        <SecondaryHeader model={model} isAddPC={isAddPC} />
        <EditStateInstructions model={model} />
        <MetroMapFrameworkView
            model={model.Framework}
            ctrl={{
                milestoneContent: m => <MilestoneContent milestone={m} />,
                milestoneHasClassname: (m, classname) =>
                    // Override the default logic for ...has-actual styling so that we base it on the draft
                    // state (e.g. are we logging/clearing actual right now) rather than the committed state.
                    classname === 'portcall-milestone--has-actual' ? !m.DisplayDateIsEstimated() :
                    undefined
            }}
        />
        <LoadingIcon model={model} />
    </div>
);

/**
 * A reusable version of the `<PortCallScheduleView />` component's header, for other components that want to
 * reuse that header styling.
 */
export const PortCallScheduleHeaderView = SuperCritical(({ model: m, className, includeItinerary = true } : {
    model : PortCallScheduleHeader,
    className? : string,
    includeItinerary? : boolean
}) =>
    <div className={cx("portcall-schedule-standalone-header", className)}>
        <MainHeader model={m} />
        <div className="portcall-schedule__header-secondary">
            <SecondaryHeaderAgentInfo model={m} />
            {includeItinerary && <ItineraryDatesOverall model={m} />}
        </div>
    </div>
);

const MainHeader = SuperCritical(({ model: m } : { model : PortCallScheduleHeader }) =>
    <div className="portcall-schedule__header-main">
        <div className="vessel-name">{m.PortCall.DisplayVesselIdentifier()}</div>
        <div className="vessel-info">
            <span className="vessel-length">{m.PortCall.VesselLength.toString({ places: 0 })}</span>
            {m.PortCall.Vessel()?.VesselType() && 
                <span>
                    <span className="vessel-length-separator">|</span>
                    <span className="vessel-type">{m.PortCall.Vessel()?.VesselType()}</span>
                </span>
            }
        </div>
    </div>
);

const SecondaryHeader = SuperCritical(({ model: m, isAddPC } : { model : PortCallSchedule, isAddPC? : boolean }) =>
    // Secondary header disappears to make space for the instructions when we're in an edit state.
    m.CurrentEditState() ? null :
        <div className="portcall-schedule__header-secondary">
            <SecondaryHeaderAgentInfo model={m.Header} />
            <MovePortCallLink model={m} isAddPC={isAddPC} />
            <ItineraryDatesOverall model={m.Header} />
        </div>
);

const SecondaryHeaderAgentInfo = SuperCritical(({ model: m } : { model : PortCallScheduleHeader }) =>
    <div className="agent-info">
        Agent:{m.PortCall.Agent() ? ` ${m.PortCall.Agent()!.AbbreviatedName()}, ${m.PortCall.Agent()!.Organization()?.Name()}` : ''}
    </div>
);

const MovePortCallLink = SuperCritical(({ model: m, isAddPC } : { model : PortCallSchedule, isAddPC?: boolean }) =>
    m.CanMovePortCall() && !isAddPC ?
        <a className="move-portcall-link" href="" onClick={noDefault(m.MovePortCall)}>
            Move PortCall
        </a> :
        null
);

// MomentJS format strings used below
const
    DAY_MONTH = 'DD MMM',
    DAY_MONTH_YEAR = `${DAY_MONTH} YYYY`;

const ItineraryDatesOverall = SuperCritical(({ model: m } : { model : PortCallScheduleHeader }) => {
    const
        startDate = m.FirstMilestone() && tzmomentMod(m.TZUtils, m.FirstMilestone()!.Date.Effective(), d => d.startOf('day')),
        endDate   = m.LastMilestone () && tzmomentMod(m.TZUtils, m.LastMilestone ()!.Date.Effective(), d => d.startOf('day')),
        formattedDateRange =
            startDate && endDate ?
                TZDate.eq(startDate, endDate) ?
                    tzmoment(startDate).format(DAY_MONTH_YEAR) :
                    `${tzmoment(startDate).format(startDate.getFullYear() === endDate.getFullYear() ? DAY_MONTH : DAY_MONTH_YEAR)} - ${tzmoment(endDate).format(DAY_MONTH_YEAR)}` :
                (startDate ?? endDate)?.format(DAY_MONTH_YEAR);
    return (
        <div className="itinerary-dates-overall">
            <span className="itinerary-dates-label">Itinerary:</span>
            <span className="itinerary-dates-range">{formattedDateRange}</span>
            <span className="itinerary-spacer">
                {' '}
                |
                {' '}
            </span>
            <span className="itinerary-port">{m.PortCall.Port()?.Name() ?? 'loading...'}</span>
        </div>
    );
});

const EditStateInstructions = SuperCritical(({ model } : { model : PortCallSchedule }) => {
    const edit = model.CurrentEditState();
    if (edit === null) return null;
    const date = edit.Date,
        pt = date.ItineraryPoint(),
        loc = pt?.Location(),
        next = pt?.NextMovementPoint(),
        nextLoc = next?.Location();
    return edit && (
        edit.Type === EditType.Move ?
            <div className="portcall-schedule__instructions">
                <h4>Move Entire PortCall</h4>
                <p>Enter a new arrival time and all other times will adjust.</p>
            </div> :
        edit.Type === EditType.EditFirstArrival ?
            <div className="portcall-schedule__instructions">
                <h4>Update {date.Actual() ? 'Actual' : 'Estimated'} Arrival - {loc?.Name()}</h4>
                <p>Enter a new time and other times will adjust as indicated.</p>
                <p>Departure times will not change.</p>
            </div> :
        edit.Type === EditType.EditBerthArrival ||
        edit.Type === EditType.EditActual && date.Type() === SDType.IPArrival ?
            <div className="portcall-schedule__instructions">
                <h4>Update {loc?.Name()} Arrival</h4>
                <p>All other times are anchored.</p>
            </div> :
        edit.Type === EditType.EditShiftDeparture ||
        edit.Type === EditType.EditActual && date.Type() === SDType.IPDeparture ?
            <div className="portcall-schedule__instructions">
                <h4>Update {loc?.Name()} Departure</h4>
                <p>Enter a new time and arrival time {nextLoc && `at ${nextLoc.Name()}`}
                    will adjust as indicated to preserve transit.</p>
            </div> :
        edit.Type === EditType.EditFinalDeparture ?
            <div className="portcall-schedule__instructions">
                <h4>Update Final Departure {loc && `from ${loc.Name()}` }</h4>
                <p>Enter a new time and outbound transit time
                    will adjust as indicated to preserve transit.</p>
            </div> :
        edit.Type === EditType.LogActual ?
            <div className="portcall-schedule__instructions">
                <h4>Log {actualLabel(date)} at {loc?.Name()}</h4>
                <p>Mark {actualLabel(date)} complete at the given time.</p>
            </div> :
        edit.Type === EditType.ClearActual ?
            <div className="portcall-schedule__instructions">
                <h4>Undo Log {actualLabel(date)} at {loc?.Name()}</h4>
                <p>Time will revert back to Estimated.</p>
            </div> :

        null
    );
});

function actualLabel(d : ScheduleDateEntity) {
    return (
        d.MilestoneTypeId() === MSType.FirstArrival ? 'Arrival' :
        d.MilestoneTypeId() === MSType.BerthArrival ? 'First Line' :
        'Last Line'
    );
}

const LoadingIcon = SuperCritical(({ model } : { model : PortCallSchedule }) =>
    model.Loading() ? <div className="loading-icon" /> : null
);

const MilestoneContent = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) =>
    <div>
        <MilestoneHeaderRow milestone={m} />
        <MilestoneScheduleDetailsRow milestone={m} />
        <MilestoneActionConfirmRow milestone={m} />
        <MilestoneCoordinationsRow milestone={m} />
    </div>
);

const MilestoneHeaderRow = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) =>
    <div className="portcall-milestone-row portcall-milestone__header-row">
        <MilestoneHeaderRowLabel milestone={m} />
        <MilestoneHeaderRowBody milestone={m} />
    </div>
);

const MilestoneHeaderRowLabel = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) =>
    <div
        className={cx('portcall-milestone-row-label', {
            'portcall-milestone-header-row-label--date-entry-rejected': m.DateEntryWasRejected()
        })}
    >
        <EditScopeIndicator milestone={m} type="date" />
        {m.DateEntry() && m.DateEntry()!() !== null && m.GlobalEditPhase() === EditPhase.Editing ?
            <DateTimePicker value={m.DateEntry() as DataSignal<TZDate>} localization={m.Localization} /> :
            <span
                className={cx(
                    TextInEditScope(m, 'date'),
                    TextNeedsReview(m, () => MilestoneHeaderDateText(m.DisplayDate()) !== MilestoneHeaderDateText(m)),
                    { 'portcall-milestone__clickable': m.CanEdit() }
                )}
                onClick={m.EditDate}
            >
                {MilestoneHeaderDateText(m.DisplayDate())}
            </span>
        }
    </div>
);

const MilestoneHeaderRowBody = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) =>
    <div className="portcall-milestone-row-body">
        <EditScopeIndicator milestone={m} type="log-or-clear-actual" />
        <span
            className={cx(
                TextInEditScope(m, 'log-or-clear-actual'),
                TextNeedsReview(m, () => DateTypeParticiple(m) !== DefaultDateTypeParticiple(m))
            )}
        >
            {DateTypeParticiple(m)}
        </span>
        {' '}
        <span className="portcall-milestone__header-berth-name">
            {/* TODO: Support change berth... */}
            {m.Date.ItineraryPoint()?.Location()?.Name()}
        </span>
        {m.CanClearActual() &&
            <a className="portcall-milestone__undo-log-actual-link" href="#" onClick={noDefault(m.ClearActual)}>
                <i className="icon-undo-circle"></i>
            </a>
        }
        {/* TEMPORARY: Hide the Log Arrival link b/c Hawaii users found it confusing. Longer-term: Make this configurable via the database.  */}
        {m.CanLogActual() && m.Date.MilestoneTypeId() !== MSType.FirstArrival &&
            <a className="portcall-milestone__log-actual-link" href="#" onClick={noDefault(m.LogActual)}>
                {`Log ${actualLabel(m.Date)}`}
            </a>
        }
    </div>
);

function DateTypeParticiple(m : PortCallMilestone) {
    return (
        m.Date.Type() === SDType.IPArrival ?
            m.DisplayDateIsEstimated() ? 'Arriving' : 'Arrived' :
        m.Date.Type() === SDType.IPDeparture  ?
            m.DisplayDateIsEstimated() ? 'Departing' : 'Departed' :
        ''
    );
}

const MilestoneScheduleDetailsRow = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) =>
    <div className="portcall-milestone-row portcall-milestone__schedule-details-row">
        <MilestoneScheduleDetailsRowLabel milestone={m} />
        <MilestoneScheduleDetailsRowBody milestone={m} />
    </div>
);

const MilestoneScheduleDetailsRowBody = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) =>
    m.Date.IsMovementStartDate() ?
        <div className="portcall-milestone-row-body">
            {m.DisplayDateIsEstimated() && <EditScopeIndicator milestone={m} type="duration" />}
            <span>
                {MovementTypeParticiple(m)}
                {' '}
                {/* TODO: Handle case of pending change to arrival berth... */}
                {m.DisplayDateIsEstimated() && MovementToLocationText(m)}
                {' '}
                &mdash;
            </span>
            {' '}
            {m.DisplayDateIsEstimated() ?
                <span
                    className={cx(
                        TextInEditScope(m, 'duration'),
                        TextNeedsReview(m, () => MovementTransitDurationText(m.DisplayDate(), m.SegmentEndDate()) !== MovementTransitDurationText(m)),
                        'pc-text-smalllabel'
                    )}
                >
                    {MovementTransitDurationText(m.DisplayDate(), m.SegmentEndDate())}
                </span> :
                <span className="portcall-milestone__log-actual-attribution">
                    {m.ChangedActual() === null && LogActualAttributionText(m)}
                </span>
            }
        </div> :
        <div className="portcall-milestone-row-body">
            <span className="portcall-milestone__log-actual-attribution">
                {m.ChangedActual() === null && LogActualAttributionText(m)}
            </span>
        </div>
);

const MilestoneActionConfirmRow = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) => {
    const edit = m.DateHasEditFocus();
    return edit && (
        <div className="portcall-milestone-row portcall-milestone__action-confirm-row">
            {
                edit.Phase() === EditPhase.Editing ? [
                    <div className="portcall-milestone-row-label update-button-shell" key="label">
                        <button className="pc-button" onClick={m.ReviewChanges}>
                            Update
                        </button>
                    </div>,
                    <div className="portcall-milestone-row-body" key="body">
                        <button className="pc-button" onClick={edit.Cancel}>
                            <i className="icon-undo-circle"></i> keep {MilestoneHeaderDateText(m)}
                        </button>
                    </div>
                ] :
                edit.Phase() === EditPhase.Reviewing || edit.Phase() === EditPhase.Committing ? [
                    <div className="portcall-milestone-row-label" key="label" />,
                    <div className="portcall-milestone-row-body" key="body">
                        <div className="pc-confirm">
                            { edit.Type === EditType.ClearActual ?
                                <span className="pc-confirm-question"><b>Undo Log {actualLabel(edit.Date)}?</b> Will revert back to Estimated.</span> :
                                <span className="pc-confirm-question"><b>Are you sure?</b> All changes are highlighted.</span>
                            }
                            <button
                                disabled={edit.Phase() === EditPhase.Committing}
                                className="pc-confirm-button pc-confirm-button-primary"
                                onClick={edit.Commit}
                            >
                                Yup
                            </button>
                            <button
                                disabled={edit.Phase() === EditPhase.Committing}
                                className="pc-confirm-button"
                                onClick={edit.Cancel}
                            >
                                Nope{ edit.Type !== EditType.ClearActual ? '.  Undo all.' : ''}
                            </button>
                        </div>
                    </div>
                ] :
                edit.Phase() === EditPhase.CommitErrors ? [
                    <div className="portcall-milestone-row-label" key="label">
                        <button className="pc-button" onClick={edit.Cancel}>
                            <i className="icon-undo-circle" /> revert
                        </button>
                    </div>,
                    <div className="portcall-milestone-row-body" key="body">
                        {ListErrors(edit.CommitErrors())}
                    </div>
                ] :
                null
            }
        </div>
    );
});

const MilestoneCoordinationsRow = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) => {
    const numObsolete = m.ObsoleteCoordinationCards().length;
    return (
        <div className="portcall-milestone-row portcall-milestone__coordinations-row">
            <MilestoneCoordinationsRowLabel milestone={m} />
            <div className="portcall-milestone-row-body">
                <BerthAssignmentView milestone={m} />
                {m.VisibleCoordinationCards().map(ccard =>
                    ccard.Currency === CCardCurrency.Current ?
                        <CurrentCCardView ccard={ccard} key={ccard.Model.Id()} /> :
                        <CancelledCCardView ccard={ccard} key={ccard.Model.Id()} />
                )}
                {numObsolete > 0 && (
                    <div className="obsolete-cards-toggle-shell">
                        <a
                            className="obsolete-cards-toggle"
                            href=""
                            onClick={noDefault(() => m.ShowObsoleteCoordinations(!m.ShowObsoleteCoordinations()))}
                        >
                            {m.ShowObsoleteCoordinations() ?
                                `Hide ${numObsolete} obsolete ${numObsolete === 1 ? 'card' : 'cards'}` :
                                `Show ${numObsolete} more obsolete ${numObsolete === 1 ? 'card' : 'cards'}`
                            }
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
});

const MilestoneCoordinationsRowLabel = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) =>
    <div className="portcall-milestone-row-label">
        {m.Date.IsBerthArrivalDate() &&
            <span>
                <EditScopeIndicator milestone={m} type="duration" />
                {/* Don't do standard edit scope text styling for berth stay duration, just highlight during
                    review as needed. */}
                <span
                    className={cx(
                        TextInEditScope(m, 'duration'),
                        TextNeedsReview(m, () => BerthStayDurationText(m.DisplayDate(), m.SegmentEndDate()) !== BerthStayDurationText(m))
                    )}
                >
                    {BerthStayDurationText(m.DisplayDate(), m.SegmentEndDate())}
                </span>
            </span>
        }
    </div>
);

const BerthAssignmentView = SuperCritical(({ milestone: m } : { milestone : PortCallMilestone }) =>
    m.BerthAssignment() &&
        // We borrow C-Card styling for berth assignments
        <div className="portcall-milestone__coordination-card">
            <BerthAssignmentHeader assignment={m.BerthAssignment()!} />
            <InPortOpsSummary assignment={m.BerthAssignment()!} />
            {m.BerthAssignment()!.Details() && <BerthAssignmentDetailsView model={m.BerthAssignment()!.Details()!} />}
        </div>
);

const BerthAssignmentHeader = SuperCritical(({ assignment: a } : { assignment : BerthAssignmentViewModel }) =>
    <div className="coordination-card-header">
        <span className="coordination-card-hitbox" onClick={a.ToggleDetails}>
            {/* TODO: Real support for cancelled reservations. Stubbing out minimal support here on the thinking that we
                might want a version of the metro map that renders cancelled reservations somehow, but as is the metro
                map should never render cancelled reservations b/c we null out the MilestoneTypeId for a reservation's
                arrival and departure times upon cancellation. */}
            {a.BerthReservation.Status() === BerthReservationStatus.Cancelled ?
                // TODO: This would be simpler if we just add support for cancelled C-Cards to our <CCardStatusIndicator />.
                <span className="cancelled-coordination-indicator pc-breaker-light pc-breaker-active" /> :
                <CCardStatusIndicator statusId={equivBerthAssignmentCCardStatus(a.BerthReservation.Status())} />
            }
            <span className="coordination-card-summary">
                <span className="coordination-title">Berth Assignment</span>
                {' '}
                <span className="coordination-status-text">({berthAssignmentStatusText(a.BerthReservation.Status())})</span>
            </span>
        </span>
        {' '}
        <IssueCountPillBadgeWithTooltip className="coordination-issue-count" issues={a.Issues} />
        <span className="coordination-card-header-right-side">
            <BookUnbookLink assignment={a} />
        </span>
    </div>
);

function equivBerthAssignmentCCardStatus(status : BerthReservationStatus) {
    if      (status === BerthReservationStatus.Booked    ) return CoordinationCardStatuses.Accepted;
    // TODO: For now, just returning a dummy value for cancelled reservations. Once we add support
    // for cancelled C-Cards to our <CCardStatusIndicator />, we can wire up to that here.
    else if (status === BerthReservationStatus.Cancelled ) return -Infinity;
    else if (status === BerthReservationStatus.Conflicted) return CoordinationCardStatuses.Denied;
    else if (status === BerthReservationStatus.Requested ) return CoordinationCardStatuses.Acknowledged;
    else throw new Error(`Unknown berth reservation status ${status}`);
}

function berthAssignmentStatusText(status : BerthReservationStatus) {
    // TODO: Support alt labels for berth reservation status, probably configured at the domain level.
    return BerthReservationStatusDisplayName[status]?.toLowerCase() ?? '--';
}

const BookUnbookLink = SuperCritical(({ assignment: a } : { assignment : BerthAssignmentViewModel }) =>
    a.CanBook() ?
        <a className="book-unbook-link" href="" onClick={noDefault(a.Book)}>Book</a> :
    a.CanUnbook() ?
        <a className="book-unbook-link" href="" onClick={noDefault(a.Unbook)}>Unbook</a> :
    null
);

const InPortOpsSummary = SuperCritical(({ assignment: a } : { assignment : BerthAssignmentViewModel }) =>
    <div className="in-port-ops-summary pc-text-tinylabel">{InPortOpsSummaryText(a.BerthReservation)}</div>
);

const EM_DASH = "\u2014";
export function InPortOpsSummaryText(br : BerthReservation) {
    if (br.HasLayOpsOnly()) {
        return "Lay Only";
    } else {
        let cargoText : string | null = null, cruiseText : string | null = null;
        if (br.CargoActivities().length > 0) {
            const acts = br.CargoActivities().slice();
            acts.sort((a1, a2) => a1.Id() - a2.Id());
            const knownCargoText = acts
                .filter(a => a.CargoCategory() !== null)
                .map(a =>
                    a.CargoCategory()!.Name()
                  + (a.ActivityType() === null ? "" : ` (${a.ActivityType()!.Name()})`)
                  + ": "
                  + a.Quantity.toString()
                )
                .join(` ${EM_DASH} `);
            cargoText = knownCargoText || "Unknown cargo";
        }
        if (br.CruiseActivities().length > 0) {
            const acts = br.CruiseActivities().slice();
            acts.sort((a1, a2) => a1.Id() - a2.Id());
            const knownCruiseText = acts
                .filter(a =>
                    (a.Disembark() ?? 0) !== 0
                 || (a.Embark   () ?? 0) !== 0
                 || (a.InTransit() ?? 0) !== 0
                )
                .map(a => [
                    (a.Disembark() ?? 0) !== 0 ? `Disembark: ${a.Disembark()}` : null,
                    (a.Embark   () ?? 0) !== 0 ? `Embark: ${a.Embark()}` : null,
                    (a.InTransit() ?? 0) !== 0 ? `In Transit: ${a.InTransit()}` : null
                ].filter(notNullish).join(` ${EM_DASH} `))
                .join(` ${EM_DASH} `);
            cruiseText = knownCruiseText || "Unknown cruise";
        }
        return [cargoText, cruiseText].filter(notNullish).join(` ${EM_DASH} `) || "No ops listed";
    }
}

const CurrentCCardView = SuperCritical(({ ccard } : { ccard : CurrentCCardViewModel }) =>
    <div className="portcall-milestone__coordination-card">
        <CurrentCCardHeader ccard={ccard} />
        {ccard.Details() && <CoordinationCardDetailsView model={ccard.Details()!} />}
    </div>
);

const CurrentCCardHeader = SuperCritical(({ ccard } : { ccard : CurrentCCardViewModel }) =>
    <div className="coordination-card-header">
        <span className="coordination-card-hitbox" onClick={ccard.ToggleDetails}>
            <CCardStatusIndicator statusId={ccard.Model.StatusId()} />
            <span className="coordination-card-summary">
                <span className="coordination-title">{ccard.Model.Coordination()?.Title()}</span>
                <span className="coordination-title-separator">|</span>
                <span className="coordination-provider">{ccard.Model.Provider()?.Provider()?.Name()}</span>
                {' '}
                <span className="coordination-status-text">({ccard.Model.EffectiveStatusLabel() ?? 'loading...'})</span>
            </span>
        </span>
    </div>
);

const CancelledCCardView = SuperCritical(({ ccard } : { ccard : CancelledCCardViewModel }) =>
    <div className="portcall-milestone__coordination-card portcall-milestone__coordination-card-cancelled">
        <CancelledCCardHeader ccard={ccard} />
        {ccard.Details() && <CancelledCoordinationCardDetailsView model={ccard.Details()!} />}
    </div>
);

const CancelledCCardHeader = SuperCritical(({ ccard } : { ccard : CancelledCCardViewModel }) =>
    <div className="coordination-card-header">
        <span className="coordination-card-hitbox" onClick={ccard.ToggleDetails}>
            {/* TODO: This would be simpler if we just add support for cancelled C-Cards to our <CCardStatusIndicator /> */}
            <span className="cancelled-coordination-indicator pc-breaker-light pc-breaker-active" />
            <span className="coordination-card-summary">
                <span className="coordination-title">{ccard.Model.Coordination()?.Title()}</span>
                <span className="coordination-title-separator">|</span>
                <span className="coordination-provider">{ccard.Model.Provider()?.Provider()?.Name()}</span>
                {' '}
                cancelled
            </span>
        </span>
    </div>
);

// TODO: Support berth change...
type EditScopeIndicatorType = 'date' | 'duration' | 'log-or-clear-actual';

const EditScopeIndicator = SuperCritical(({ milestone: m, type } : { milestone : PortCallMilestone, type : EditScopeIndicatorType }) =>
    ShowEditScopeIndicator(m, type) ?
        <i
            className={cx('portcall-milestone__edit-scope-indicator', {
                'portcall-milestone__edit-scope-indicator-emphasized': EmphasizeEditScopeIndicator(m, type)
            })}
        >
            {/* Render both a left and a right caret, and we'll use CSS to show only the appropriate one based
                on which column of the metro map this indicator is included within. */}
            <span className="fa fa-caret-left" />
            <span className="fa fa-caret-right" />
        </i> :
        null
);

function ShowEditScopeIndicator(m : PortCallMilestone, type : EditScopeIndicatorType) {
    return m.GlobalEditPhase() === EditPhase.Editing && (
        type === 'date'                && m.DateIsInEditScope()
     || type === 'duration'            && m.DurationIsInEditScope()
     || type === 'log-or-clear-actual' && (m.DateHasEditFocus()?.Type === EditType.LogActual || m.DateHasEditFocus()?.Type === EditType.ClearActual)
    );
}

function EmphasizeEditScopeIndicator(m : PortCallMilestone, type : EditScopeIndicatorType) {
    return (
        type === 'date'     && m.DateHasEditFocus() !== null
    );
}

function TextInEditScope(m : PortCallMilestone, type : EditScopeIndicatorType) {
    return { 'portcall-milestone__text-in-edit-scope': ShowEditScopeIndicator(m, type) };
}

function TextNeedsReview(m : PortCallMilestone, hasPendingChange : () => boolean) {
    const phase = m.GlobalEditPhase();
    return {
        'portcall-milestone__text-needs-review':
            (
                phase === EditPhase.Reviewing
             || phase === EditPhase.Committing
             || phase === EditPhase.CommitErrors
            ) && hasPendingChange()
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/portcallschedule/view.tsx